import { useMemo } from 'react';
import lightningUrl from '@/assets/svg/lightning.svg';
import type { LightningFrequency } from '@/types/weather';

interface Props {
  lightning: boolean;
  frequency: LightningFrequency;
}

interface BoltInstance {
  id: number;
  left: number;
  top: number;
  scale: number;
  delay: number;
  duration: number;
  rotation: number;
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed + 7) * 10000;
  return x - Math.floor(x);
}

export function LightningLayer({ lightning, frequency }: Props) {
  const bolts = useMemo<BoltInstance[]>(() => {
    if (!lightning) return [];
    const count = frequency === 'frequent' ? 3 : 2;
    const baseDuration = frequency === 'frequent' ? 3 : 6;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: 15 + seededRandom(i * 11) * 60,
      top: 5 + seededRandom(i * 17) * 30,
      scale: 0.6 + seededRandom(i * 23) * 0.5,
      delay: seededRandom(i * 29) * baseDuration,
      duration: baseDuration + seededRandom(i * 37) * 2,
      rotation: -70 + seededRandom(i * 41) * 20,
    }));
  }, [lightning, frequency]);

  if (!lightning) return null;

  const animName = frequency === 'frequent' ? 'lightningFlashFrequent' : 'lightningFlash';

  return (
    <div className="layer" style={{ zIndex: 70 }}>
      {bolts.map((bolt) => (
        <img
          key={bolt.id}
          src={lightningUrl}
          alt=""
          style={{
            position: 'absolute',
            left: `${bolt.left}%`,
            top: `${bolt.top}%`,
            width: 'clamp(120px, 16vw, 260px)',
            height: 'auto',
            transform: `scale(${bolt.scale}) rotate(${bolt.rotation}deg)`,
            transformOrigin: 'top center',
            animationName: animName,
            animationDuration: `${bolt.duration}s`,
            animationDelay: `${bolt.delay}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            opacity: 0,
          }}
        />
      ))}
    </div>
  );
}
